import React, { useCallback, useMemo, useState } from 'react';
import { BlackBtn } from './AuthShared';
import {
  buildCibSuccessInfos,
  formatPaymentDetails,
  pickPaymentDetails,
  sendCheckoutReceiptEmail,
} from '../services/checkoutApi';
import { buildCheckoutReceiptPdf, receiptPdfFileName } from '../utils/buildCheckoutReceiptPdf';

export function buildInvoicePdfLabels(t) {
  return {
    invoice: t.checkout_invoice_title,
    store: t.checkout_invoice_store,
    product: t.checkout_invoice_product,
    type: t.checkout_invoice_type,
    period: t.checkout_invoice_period,
    amount: t.checkout_invoice_amount,
    promo: t.checkout_invoice_promo,
    subtotal: t.checkout_invoice_subtotal,
    discount: t.checkout_invoice_discount,
    total: t.checkout_invoice_total,
    payment: t.checkout_invoice_payment,
    paid: t.checkout_invoice_paid,
    signature: t.checkout_invoice_signature,
    website: t.checkout_invoice_website,
    packType: t.checkout_pack_label,
    domainType: t.checkout_domain_label,
    monthly: t.checkout_invoice_period_monthly,
    annual: t.checkout_invoice_period_annual,
  };
}

export function buildCheckoutInvoiceMeta({
  user,
  currentStore,
  isDomain,
  domain,
  title,
  selectedPack,
  isAnnualBilling,
  currency,
  amount,
  baseAmount,
  appliedPromo,
}) {
  const customerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const discountAmount = baseAmount != null && amount != null && baseAmount > amount
    ? baseAmount - amount
    : null;
  const productName = isDomain
    ? domain
    : (selectedPack?.name?.replace('-mois', '').replace('-an', '') || title);

  return {
    customerName,
    customerEmail: user?.email || '',
    storeName: currentStore?.name || '',
    productType: isDomain ? 'domain' : 'pack',
    productName,
    billingPeriod: isDomain || isAnnualBilling ? 'annual' : 'monthly',
    currency,
    totalAmount: amount,
    baseAmount,
    promoCode: appliedPromo?.code || null,
    discountAmount,
  };
}

export function buildInvoiceMetaFromCartOrder(user, cartOrder) {
  if (!cartOrder) {
    return {
      customerName: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
      customerEmail: user?.email || '',
    };
  }
  const item = cartOrder.item;
  const raw = String(item?.value ?? item?.name ?? '');
  const isDomain = cartOrder.type === 'domain' || /\.[a-z]{2,}$/i.test(raw);
  const discount = cartOrder.reduction_total != null ? Number(cartOrder.reduction_total) : null;

  return {
    customerName: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
    customerEmail: user?.email || '',
    productType: isDomain ? 'domain' : 'pack',
    productName: isDomain ? raw : raw.split('-')[0] || raw,
    billingPeriod: raw.includes('-mois') ? 'monthly' : 'annual',
    currency: cartOrder.gross_total_currency || 'DZD',
    totalAmount: cartOrder.gross_total ?? cartOrder.net_total,
    baseAmount: cartOrder.net_total,
    promoCode: cartOrder.reduction_code || null,
    discountAmount: discount > 0 ? discount : null,
  };
}

function CheckIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('fr-DZ');
}

export function CibVerifyingPanel({ t }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px 12px' }}>
      <span className="kio-spinner-dark" style={{ display: 'inline-block', marginBottom: 16 }} />
      <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#111' }}>{t.checkout_payment_checking}</p>
      <p style={{ margin: '8px 0 0', fontSize: 13, color: '#616161', lineHeight: 1.5 }}>
        {t.checkout_cib_verifying_sub}
      </p>
    </div>
  );
}

export function CibSuccessPanel({
  t,
  result,
  onContinue,
  userEmail = '',
  orderId = '',
  cartOrder = null,
  successInfos: successInfosProp = null,
  invoiceMeta = null,
}) {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [email, setEmail] = useState(userEmail || '');
  const [emailSending, setEmailSending] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [receiptNotice, setReceiptNotice] = useState(null);
  const [emailNotice, setEmailNotice] = useState(null);

  const details = formatPaymentDetails(result);
  const cibDetails = pickPaymentDetails(result);

  const successInfos = useMemo(() => {
    if (successInfosProp?.length) return successInfosProp;
    return buildCibSuccessInfos(cibDetails, orderId, t);
  }, [successInfosProp, cibDetails, orderId, t]);

  const receiptRows = useMemo(() => {
    if (successInfos.length) {
      return successInfos.map((row) => [row.label, row.value]);
    }
    return [
      [t.checkout_cib_detail_amount, details.amountLabel || '—'],
      [t.checkout_cib_detail_order, details.orderNumber || '—'],
      [t.checkout_cib_detail_auth, details.authorization || '—'],
      [t.checkout_cib_detail_date, formatDisplayDate(details.date)],
    ];
  }, [successInfos, details, t]);

  const pdfFileName = receiptPdfFileName(details.orderNumber);

  const buildReceiptPdf = useCallback(async () => {
    const bytes = await buildCheckoutReceiptPdf({
      title: t.checkout_cib_success_title,
      invoiceNumber: details.orderNumber || orderId,
      paymentRows: receiptRows,
      cartOrder,
      invoiceMeta: {
        ...(invoiceMeta || {}),
        labels: buildInvoicePdfLabels(t),
      },
    });
    return new Blob([bytes], { type: 'application/pdf' });
  }, [t, details.orderNumber, orderId, receiptRows, cartOrder, invoiceMeta]);

  const withPdfBlob = useCallback(async (action, fallbackError) => {
    setPdfLoading(true);
    setReceiptNotice(null);
    try {
      const blob = await buildReceiptPdf();
      await action(blob);
    } catch (err) {
      setReceiptNotice({
        error: true,
        message: err?.message || fallbackError || t.checkout_cib_receipt_error,
      });
    } finally {
      setPdfLoading(false);
    }
  }, [buildReceiptPdf, t.checkout_cib_receipt_error]);

  const handlePrint = () => {
    withPdfBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) {
        win.addEventListener('load', () => {
          win.focus();
          win.print();
        });
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
      }
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          iframe.remove();
          URL.revokeObjectURL(url);
        }, 1000);
      };
    }, t.checkout_cib_print_blocked);
  };

  const handleDownload = () => {
    withPdfBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfFileName;
      link.click();
      URL.revokeObjectURL(url);
    }, t.checkout_cib_receipt_error);
  };

  const handleSendEmail = async () => {
    if (!email.trim()) {
      setEmailNotice({ error: true, message: t.checkout_cib_email_required });
      return;
    }
    setEmailSending(true);
    setEmailNotice(null);
    try {
      const pdfBlob = await buildReceiptPdf();
      const file = new File([pdfBlob], 'invoice.pdf', {
        type: 'application/pdf',
        lastModified: Date.now(),
      });
      await sendCheckoutReceiptEmail({ file, email: email.trim() });
      setEmailModalOpen(false);
      setEmailNotice({ error: false, message: t.checkout_cib_email_sent });
    } catch (err) {
      setEmailNotice({
        error: true,
        message: err?.message || t.checkout_cib_email_error,
      });
    } finally {
      setEmailSending(false);
    }
  };

  const showReceiptActions = successInfos.length > 0;
  const receiptBusy = pdfLoading || emailSending;

  return (
    <div>
      <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: '#ecfdf3',
          border: '1px solid #86efac', display: 'flex', alignItems: 'center',
          justifyContent: 'center', margin: '0 auto 16px', color: '#15803d',
        }}>
          <CheckIcon size={28} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>
          {t.checkout_cib_success_title}
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: '#616161', lineHeight: 1.55 }}>
          {details.message || t.checkout_cib_success_sub}
        </p>
      </div>

      <div style={{
        borderTop: '1px solid #f0f0f0', paddingTop: 18,
        background: '#fafafa', borderRadius: 10, padding: '14px 16px',
      }}>
        {successInfos.map((row) => (
          <div
            key={row.label}
            style={{
              display: 'flex', justifyContent: 'space-between', gap: 12,
              padding: '8px 0', borderBottom: '1px solid #ececec',
            }}
          >
            <span style={{ fontSize: 13, color: '#616161' }}>{row.label}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#111', textAlign: 'right' }}>{row.value}</span>
          </div>
        ))}
      </div>

      {showReceiptActions && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 8, marginTop: 20,
        }}>
          <button type="button" onClick={handlePrint} disabled={receiptBusy} style={actionBtnStyle}>
            {pdfLoading ? <span className="kio-spinner-dark" /> : t.checkout_cib_print}
          </button>
          <button type="button" onClick={handleDownload} disabled={receiptBusy} style={actionBtnStyle}>
            {pdfLoading ? <span className="kio-spinner-dark" /> : t.checkout_cib_download}
          </button>
          <button
            type="button"
            onClick={() => { setEmailNotice(null); setEmailModalOpen(true); }}
            disabled={receiptBusy}
            style={actionBtnStyle}
          >
            {t.checkout_cib_email}
          </button>
        </div>
      )}

      {receiptNotice && (
        <p style={{
          margin: '12px 0 0', fontSize: 12, lineHeight: 1.5,
          color: receiptNotice.error ? '#d72c0d' : '#1a7f37',
        }}>
          {receiptNotice.message}
        </p>
      )}

      {emailNotice && (
        <p style={{
          margin: '12px 0 0', fontSize: 12, lineHeight: 1.5,
          color: emailNotice.error ? '#d72c0d' : '#1a7f37',
        }}>
          {emailNotice.message}
        </p>
      )}

      <div style={{ marginTop: 20 }}>
        <BlackBtn onClick={onContinue}>{t.checkout_cib_go_admin}</BlackBtn>
      </div>

      {emailModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10001,
          background: 'rgba(17,17,17,0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            width: '100%', maxWidth: 420, background: '#fff',
            borderRadius: 12, border: '1px solid #e3e3e3', padding: '20px 22px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
          }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#111' }}>
              {t.checkout_cib_email_modal_title}
            </h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#616161' }}>
              {pdfFileName}
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.checkout_cib_email_placeholder}
              autoComplete="email"
              style={{
                width: '100%', boxSizing: 'border-box',
                border: '1px solid #c9cccf', borderRadius: 8,
                padding: '10px 12px', fontSize: 13, marginBottom: 16,
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setEmailModalOpen(false)}
                style={secondaryBtnStyle}
                disabled={emailSending}
              >
                {t.checkout_back}
              </button>
              <button
                type="button"
                onClick={handleSendEmail}
                style={{ ...primaryBtnStyle, minWidth: 100 }}
                disabled={emailSending}
              >
                {emailSending ? <span className="kio-spinner-dark" /> : t.checkout_cib_email_send}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CibFailurePanel({ t, message, onBackPricing, onRetry }) {
  return (
    <div style={{ textAlign: 'center', padding: '8px 0 12px' }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%', background: '#fef2f2',
        border: '1px solid #fecaca', display: 'flex', alignItems: 'center',
        justifyContent: 'center', margin: '0 auto 16px',
      }}>
        <XIcon />
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>
        {t.checkout_cib_failed_title}
      </h2>
      <p style={{ margin: 0, fontSize: 14, color: '#616161', lineHeight: 1.55 }}>
        {message || t.checkout_cib_failed_sub}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
        {onRetry && (
          <BlackBtn onClick={onRetry}>{t.checkout_cib_retry}</BlackBtn>
        )}
        <button type="button" onClick={onBackPricing} style={secondaryBtnStyle}>
          {t.checkout_cib_back_pricing}
        </button>
      </div>
    </div>
  );
}

const actionBtnStyle = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #e3e3e3',
  background: '#fff',
  fontSize: 12,
  fontWeight: 600,
  color: '#303030',
  cursor: 'pointer',
  minHeight: 40,
};

const secondaryBtnStyle = {
  padding: '10px 16px',
  background: '#fff',
  border: '1.5px solid #e3e3e3',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  color: '#303030',
};

const primaryBtnStyle = {
  padding: '10px 16px',
  background: '#111',
  border: 'none',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  color: '#fff',
};
